import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { format } from 'date-fns';
import { useUserStore } from '@/store/userStore';

export function useTodaySteps() {
  const { profile } = useUserStore();
  const [steps, setSteps] = useState<number>(0);
  const [caloriesBurned, setCaloriesBurned] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const today = format(new Date(), 'yyyy-MM-dd');
  const refetch = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('steps_log')
      .select('*')
      .eq('user_id', profile.id)
      .eq('logged_at', today)
      .maybeSingle();
    
    if (data) {
      setSteps(data.steps);
      setCaloriesBurned(data.calories_burned);
    } else {
      setSteps(0);
      setCaloriesBurned(0);
    }
    setLoading(false);
  }, [today, profile?.id]);
  useEffect(() => {
    refetch();
  }, [refetch]);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetch();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', refetch);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', refetch);
    };
  }, [refetch]);
  return { steps, caloriesBurned, loading, refetch };
}

export function calcCaloriesFromSteps(steps: number, weightKg = 70): number {
  return Math.round(steps * 0.04 * (weightKg / 70));
}

/**
 * Логирует шаги за указанную дату + начисляет XP + проверяет ачивки.
 */
export async function logSteps(
  steps: number, 
  weightKg = 70,
  date?: string
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Не авторизован');
  
  const targetDate = date || format(new Date(), 'yyyy-MM-dd');
  const calories_burned = calcCaloriesFromSteps(steps, weightKg);
  
  await supabase.from('steps_log').upsert({
    user_id: user.id,
    steps,
    calories_burned,
    logged_at: targetDate,
  }, { onConflict: 'user_id,logged_at' });
  
  // 🆕 Геймификация — асинхронно
  awardStepsGamification(user.id, steps, targetDate).catch(err => {
    console.error('[logSteps] gamification error:', err);
  });
}

// 🆕 Геймификация после записи шагов
async function awardStepsGamification(userId: string, steps: number, dateStr: string) {
  try {
    // 1. +3 XP за запись шагов
    await supabase.rpc('add_xp', { uid: userId, amount: 3 });
    
    // 2. Если >=10000 — ачивка steps_10k
    if (steps >= 10000) {
      await supabase.rpc('check_achievement', { 
        uid: userId, 
        ach_id: 'steps_10k', 
        new_progress: 1 
      });
      
      // 3. Проверяем steps_week — считаем сколько дней подряд по >=10к
      // За последние 7 дней — сколько было дней с >=10000 шагов
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];
      
      const { count } = await supabase
        .from('steps_log')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('logged_at', sevenDaysAgoStr)
        .gte('steps', 10000);
      
      if (count) {
        await supabase.rpc('check_achievement', { 
          uid: userId, 
          ach_id: 'steps_week', 
          new_progress: count 
        });
      }
    }
  } catch (e) {
    console.error('[awardStepsGamification] error:', e);
  }
}
